import { put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import { FormApi } from 'final-form';

import { fetchAddFeedback } from 'APIPackage/MainModule/services/feedbackApi';
import { INTERNAL_SERVER_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import { FEEDBACK_SEND_SUCCESSFULLY } from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IUploadFile } from 'CorePackage/MainModule/interfaces/IUploadFile';
import { fetchUploadFile } from 'APIPackage/MainModule/services/uploadApi';
import { UploadResponse } from 'APIPackage/MainModule/types/UploadResponse';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { feedbackPost, feedbackReset, feedbackSetFiles, feedbackUploadFile } from '../actions/feedbackActions';
import { FeedbackActionsEnum } from '../enums/FeedbackActionsEnum';
import { getFeedbackFiles, getFeedbackForm } from '../selectors/feedbackSelectors';
import { FeedbackFormType } from '../types/FeedbackFormType';

export function* watchFeedbackSaga() {
  yield takeEvery(FeedbackActionsEnum.FEEDBACK_POST, feedbackPostSaga);
  yield takeEvery(FeedbackActionsEnum.FEEDBACK_UPLOAD_FILE, feedbackUploadFileSaga);
}

const getUploadedFileIds = (uploadedFiles: IUploadFile[]) =>
  uploadedFiles.reduce((ids: number[], { fileId }) => {
    if (fileId) {
      return [...ids, fileId];
    }

    return ids;
  }, []);

function* feedbackPostSaga(action: ActionType<typeof feedbackPost>) {
  function* funcTry() {
    const { values, files, textDescription } = action.payload;
    const form: FormApi<FeedbackFormType, Partial<FeedbackFormType>> | undefined = yield select(getFeedbackForm);

    yield fetchAddFeedback({
      lastName: values.lastName,
      firstName: values.firstName,
      patronymic: values.patronymic,
      email: values.email,
      phone: values.phone === '' ? '' : `+7${values.phone}`,
      text: textDescription,
      topic: values.topic,
      organizationName: values.organizationName,
      position: values.position,
      declarantType: values.declarantType?.id ?? '',
      attachmentIds: getUploadedFileIds(files),
      knmId: values.knmId,
      planId: values.planId,
    });
    form?.setConfig('keepDirtyOnReinitialize', false);
    form?.restart();
    form?.setConfig('keepDirtyOnReinitialize', true);
    yield put(
      addNoticeAction({
        renderContent: FEEDBACK_SEND_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.SEND_FEEDBACK,
      }),
    );
    yield put(feedbackReset());
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* feedbackUploadFileSaga(action: ActionType<typeof feedbackUploadFile>) {
  function* funcTry() {
    const { e, nameIfExists } = action.payload;
    const files: IUploadFile[] = yield select(getFeedbackFiles);
    const fileList = e.target.files || [];
    const filesArray = Array.from(fileList);
    const file = filesArray[0];
    const fileName = nameIfExists || file.name;
    const fileType = file.type;
    const fileSize = file.size;
    const data = new FormData();

    data.append('file', file);
    const uploadingFiles = [
      ...files,
      {
        fileId: null,
        fileName,
        fileSize,
        fileType,
        progress: 0,
      },
    ];

    yield put(feedbackSetFiles(uploadingFiles));

    const responseData: UploadResponse = yield fetchUploadFile(data);
    const fileIndex = uploadingFiles.findIndex(f => f.fileName === fileName);
    const uploadedFiles = [
      ...uploadingFiles.slice(0, fileIndex),
      {
        ...uploadingFiles[fileIndex],
        fileName,
        fileId: responseData,
        progress: 100,
        success: true,
      },
      ...uploadingFiles.slice(fileIndex + 1),
    ];

    yield put(feedbackSetFiles(uploadedFiles));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}
